import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { 
  ChevronRight, Trash2, Minus, Plus, ShoppingCart, 
  ArrowRight, CreditCard, ShieldCheck, Upload, X, FileText, Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORM_STORAGE_KEY = 'gg_checkout_form';
const MAX_FILES = 10;
const MAX_FILE_MB = 50;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const MAX_QUANTITY = 100;

const CartPage = () => {
  const { cart, updateCartItem, removeFromCart, clearCart, cartTotal, loading: cartLoading } = useCart();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [adReps, setAdReps] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    ad_instructions: '',
    ad_rep_id: ''
  });
  const [files, setFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Load form data from localStorage on mount
  useEffect(() => {
    const savedForm = localStorage.getItem(FORM_STORAGE_KEY);
    if (savedForm) {
      try {
        const parsed = JSON.parse(savedForm);
        setCustomerInfo(parsed.customerInfo || {
          name: '',
          company: '',
          email: '',
          phone: '',
          ad_instructions: '',
          ad_rep_id: ''
        });
        // Note: Files are not restored from localStorage due to size constraints
      } catch (e) {
        console.error('Error loading saved form:', e);
      }
    }
  }, []);

  // Save form data to localStorage on change
  useEffect(() => {
    const formData = {
      customerInfo,
      timestamp: Date.now()
    };
    localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
  }, [customerInfo]);

  // Fetch ad reps
  useEffect(() => {
    const fetchAdReps = async () => {
      try {
        const response = await axios.get(`${API}/ad-reps`);
        setAdReps(response.data);
      } catch (error) {
        console.error('Error fetching ad reps:', error);
      }
    };
    fetchAdReps();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(productId);
    } else {
      await updateCartItem(productId, newQuantity);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    // Reset the input so the same file can be picked again after removing it
    e.target.value = '';

    if (files.length + selectedFiles.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const tooLarge = selectedFiles.filter(file => file.size > MAX_FILE_BYTES);
    if (tooLarge.length > 0) {
      toast.error(`${tooLarge.map(f => f.name).join(', ')} ${tooLarge.length === 1 ? 'is' : 'are'} over ${MAX_FILE_MB} MB. Please email large files to ads@gringogazette.com.`);
    }
    const accepted = selectedFiles.filter(file => file.size <= MAX_FILE_BYTES);

    setFiles(prev => [...prev, ...accepted.map(file => ({
      file,
      filename: file.name,
      size: file.size,
      type: file.type
    }))]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Calculate totals with discounts
  const calculateTotals = () => {
    let subtotal = 0;
    let totalSavings = 0;
    
    cart.items?.forEach(item => {
      const product = item.product;
      if (product) {
        const finalPrice = product.final_price || product.price;
        subtotal += finalPrice * item.quantity;
        
        if (product.is_on_sale && product.discount_valid) {
          totalSavings += (product.price - finalPrice) * item.quantity;
        }
      }
    });
    
    return { subtotal, totalSavings };
  };

  const { subtotal, totalSavings } = calculateTotals();

  const handleCheckout = async () => {
    if (!customerInfo.name.trim() || !customerInfo.email.trim()) {
      toast.error('Please fill in your full name and email');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setCheckoutLoading(true);
    try {
      // Upload files one per request so no single request carries every file
      let fileIds = [];
      if (files.length > 0) {
        setUploadingFiles(true);
        try {
          for (const f of files) {
            const formData = new FormData();
            formData.append('files', f.file);
            const uploadRes = await axios.post(`${API}/upload-files`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            fileIds = fileIds.concat(uploadRes.data.files.map(u => u.id));
          }
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
          const detail = uploadError.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : 'A file failed to upload. Please try again.');
          setUploadingFiles(false);
          setCheckoutLoading(false);
          return;
        }
        setUploadingFiles(false);
      }

      const response = await axios.post(`${API}/checkout`, {
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        company: customerInfo.company || null,
        phone: customerInfo.phone || null,
        ad_instructions: customerInfo.ad_instructions || null,
        ad_rep_id: customerInfo.ad_rep_id || null,
        file_ids: fileIds,
        items: cart.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        origin_url: window.location.origin
      });

      // Clear localStorage after successful checkout initiation
      localStorage.removeItem(FORM_STORAGE_KEY);
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  if (cart.items?.length === 0) {
    return (
      <div className="min-h-screen bg-white" data-testid="cart-page-empty">
        <div className="container mx-auto px-4 py-16 md:py-24 text-center">
          <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
            Your Cart is Empty
          </h1>
          <p className="text-slate-600 mb-8 max-w-md mx-auto">
            Browse our advertising options and add some products to your cart.
          </p>
          <Link to="/products">
            <Button className="bg-navy-900 hover:bg-navy-800" data-testid="browse-products-btn">
              Browse Products
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cart-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-500 hover:text-navy-700">Home</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <span className="text-slate-900 font-medium">Shopping Cart</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-8" data-testid="cart-title">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-7">
            <Card className="mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl">
                  {cart.items.length} {cart.items.length === 1 ? 'Item' : 'Items'}
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    clearCart();
                    toast.success('Cart cleared');
                  }}
                  data-testid="clear-cart-btn"
                >
                  Clear Cart
                </Button>
              </CardHeader>
              <CardContent className="divide-y">
                {cart.items.map((item) => {
                  const isOnSale = item.product?.is_on_sale && item.product?.discount_valid;
                  const finalPrice = item.product?.final_price || item.product?.price || 0;
                  
                  return (
                    <div 
                      key={item.product_id} 
                      className="py-4 first:pt-0 last:pb-0"
                      data-testid={`cart-item-${item.product_id}`}
                    >
                      <div className="flex gap-4">
                        <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 relative">
                          <img
                            src={item.product?.image_url}
                            alt={item.product?.name}
                            className="w-full h-full object-cover"
                          />
                          {isOnSale && (
                            <Badge className="absolute top-1 right-1 bg-red-600 text-white text-xs px-1.5">
                              {item.product?.discount_percentage}%
                            </Badge>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link 
                            to={`/product/${item.product_id}`}
                            className="font-heading font-semibold text-slate-900 hover:text-navy-700 line-clamp-1"
                          >
                            {item.product?.name}
                          </Link>
                          <div className="flex items-center gap-2 mt-1">
                            {isOnSale ? (
                              <>
                                <span className="text-sm text-red-600 font-medium">{formatPrice(finalPrice)}</span>
                                <span className="text-sm text-slate-400 line-through">{formatPrice(item.product?.price)}</span>
                                <Badge variant="secondary" className="text-xs bg-red-100 text-red-700">
                                  <Tag className="h-3 w-3 mr-1" />
                                  {item.product?.discount_percentage}% OFF
                                </Badge>
                              </>
                            ) : (
                              <span className="text-sm text-slate-500">{formatPrice(finalPrice)} each</span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-3">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                                disabled={cartLoading}
                                data-testid={`cart-minus-${item.product_id}`}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-8 text-center font-medium">{item.quantity}</span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                                disabled={cartLoading || item.quantity >= MAX_QUANTITY}
                                data-testid={`cart-plus-${item.product_id}`}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                removeFromCart(item.product_id);
                                toast.success('Item removed');
                              }}
                              disabled={cartLoading}
                              data-testid={`cart-remove-${item.product_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`font-heading font-bold text-lg ${isOnSale ? 'text-red-600' : 'text-navy-900'}`}>
                            {formatPrice(finalPrice * item.quantity)}
                          </span>
                          {isOnSale && (
                            <p className="text-xs text-green-600">
                              Save {formatPrice((item.product?.price - finalPrice) * item.quantity)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Checkout Form & Summary */}
          <div className="lg:col-span-5">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Complete Your Order</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Info text */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                  <p className="font-medium mb-2">Please fill in the following fields</p>
                  <p>You can attach photos, illustrations, logos for your ad or a completely designed ad. If you don't have an ad designed, we can take care of that at no extra cost.</p>
                </div>

                {/* Customer Info */}
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company">Company (optional)</Label>
                    <Input
                      id="company"
                      placeholder="Your Company Name"
                      value={customerInfo.company}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, company: e.target.value })}
                      data-testid="customer-company-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                      data-testid="customer-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 234 567 8900"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                      data-testid="customer-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ad_rep">Ad Rep</Label>
                    <Select
                      value={customerInfo.ad_rep_id || "none"}
                      onValueChange={(value) => setCustomerInfo({ ...customerInfo, ad_rep_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="ad-rep-select">
                        <SelectValue placeholder="Select Ad Rep (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {adReps.map((rep) => (
                          <SelectItem key={rep.id} value={rep.id}>
                            {rep.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="ad_instructions">Ad Instructions (optional)</Label>
                    <Textarea
                      id="ad_instructions"
                      placeholder="Describe your ad requirements, text content, special requests, etc."
                      value={customerInfo.ad_instructions}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, ad_instructions: e.target.value })}
                      rows={4}
                      data-testid="customer-instructions-input"
                    />
                  </div>

                  {/* File Upload */}
                  <div>
                    <Label>Files (up to {MAX_FILES} files, {MAX_FILE_MB} MB each)</Label>
                    <div 
                      className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-navy-400 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">Click to upload files</p>
                      <p className="text-xs text-slate-400 mt-1">Images, PDFs, or design files</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.ai,.psd,.eps,.svg"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="file-upload-input"
                    />
                    
                    {/* File List */}
                    {files.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                              <span className="text-sm text-slate-700 truncate">{file.filename}</span>
                              <span className="text-xs text-slate-400">({formatFileSize(file.size)})</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-slate-400 hover:text-red-600"
                              onClick={() => removeFile(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Price breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  {totalSavings > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount Savings</span>
                      <span>-{formatPrice(totalSavings)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-600">
                    <span>Tax</span>
                    <span>$0</span>
                  </div>
                </div>

                <Separator />

                <div className="flex justify-between items-center">
                  <span className="font-semibold text-slate-900">Total</span>
                  <span className="font-heading text-2xl font-bold text-navy-900" data-testid="cart-total">
                    {formatPrice(subtotal)}
                  </span>
                </div>

                <Button
                  className="w-full h-12 bg-navy-900 hover:bg-navy-800 text-white font-semibold"
                  onClick={handleCheckout}
                  disabled={checkoutLoading || cartLoading}
                  data-testid="checkout-btn"
                >
                  {checkoutLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner h-4 w-4 border-white border-t-transparent" />
                      {uploadingFiles ? 'Uploading files...' : 'Processing...'}
                    </span>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Proceed to Checkout
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                  <ShieldCheck className="h-4 w-4" />
                  Secure checkout powered by Stripe
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
